import { MAX_NOTES_PER_BULK_APPEND } from '../thunks/work_orders_thunks';

/**
 * Sending a photo selection as notes is the same job in every composer: chunk the links so a large
 * selection is not rejected by the backend's bulk cap, keep going when a chunk fails, and report
 * which links still need a retry so the preview modal can hold on to them. Only the side effects
 * around each send differ per composer, so those are passed in.
 */
export interface SendNotesInBatchesParams<TNote> {
	/** Uploaded photo links, in the order the user picked them. */
	links: string[];
	/** Turns one link into the note payload the sender expects. */
	buildNote: (link: string) => TNote;
	/**
	 * Bulk sender. When given, links go out in chunks of `MAX_NOTES_PER_BULK_APPEND` — one request
	 * and one grouped notification per chunk, rather than one per photo.
	 */
	sendBulk?: (notes: TNote[]) => Promise<any>;
	/**
	 * Single-note sender, used when there is no bulk sender available. Notes go out sequentially so
	 * they land in the order the user picked them.
	 */
	sendOne?: (note: TNote) => Promise<any>;
	/** Runs after each chunk (or note) that lands, for per-composer side effects. */
	onBatchSent?: (data: any, sentLinks: string[]) => void;
	/** Prefix for the `console.error` logged when a chunk is rejected. */
	errorLabel: string;
}

export interface SendNotesInBatchesResult {
	/** Links whose send was rejected, so the caller can keep a retryable selection. */
	failedLinks: string[];
	/** Response from the last send that resolved, for callers that pass it on to a `onNoteSent`. */
	lastData: any;
}

/**
 * Send one note per link, in chunks when a bulk sender is available. A rejected chunk is recorded
 * rather than thrown, so the remaining chunks still get their chance and the caller ends up with
 * the full list of links that need retrying.
 */
export const sendNotesInBatches = async <TNote>({
	links,
	buildNote,
	sendBulk,
	sendOne,
	onBatchSent,
	errorLabel,
}: SendNotesInBatchesParams<TNote>): Promise<SendNotesInBatchesResult> => {
	const failedLinks: string[] = [];
	let lastData: any;

	const recordSend = (data: any, sentLinks: string[]) => {
		lastData = data;
		if (onBatchSent) {
			onBatchSent(data, sentLinks);
		}
	};

	if (sendBulk) {
		for (let start = 0; start < links.length; start += MAX_NOTES_PER_BULK_APPEND) {
			const batch = links.slice(start, start + MAX_NOTES_PER_BULK_APPEND);
			try {
				recordSend(await sendBulk(batch.map(buildNote)), batch);
			} catch (error) {
				failedLinks.push(...batch);
				console.error(`${errorLabel}:`, error);
			}
		}
	} else if (sendOne) {
		for (const link of links) {
			try {
				recordSend(await sendOne(buildNote(link)), [link]);
			} catch (error) {
				failedLinks.push(link);
				console.error(`${errorLabel}:`, error);
			}
		}
	}

	return { failedLinks, lastData };
};

/**
 * The message shown when part of a selection did not send. Pluralised on the size of the selection
 * the user made, not on the failure count, so the wording matches the batch they are looking at.
 */
export const formatPhotoSendFailureMessage = (failedCount: number, totalCount: number): string =>
	`${failedCount} of ${totalCount} photo${
		totalCount > 1 ? 's' : ''
	} failed to send. Please try again.`;
